import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import DateFnsUtils from '@date-io/date-fns'
import enUS from 'date-fns/locale/en-US'
import arSA from 'date-fns/locale/ar-SA'
import { TextField, InputAdornment, InputLabel, MenuItem, Select, Button, IconButton } from '@material-ui/core'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { DatePicker } from '@material-ui/pickers'
import EventIcon from '@material-ui/icons/Event'
import { LanguageConsumer } from '../../context/LanguageContext'
import { ListingConsumer } from '../../context/ListingContext'
import { FormLoader } from '../Loader/FormLoader'
import GeneralTableWithFilter from '../GeneralTableWithFilter'
import { useStyles } from './Styles'
import codeStatus from '../../configurations/code-status'
import 'moment/locale/ar-sa'
import Moment from 'react-moment'
import { getStatusIcon } from '../../utils/helpers'
import EditIcon from '@material-ui/icons/Edit'
import CookiesStorageService from '../../Services/CookiesStorageService'


const CodesRequestsApprovalContent = ({ listingData, locale, openDialog, loadCodeData }) => {
  const classes = useStyles()
  const intl = useIntl()
  const submitFilterRef = React.useRef(null)

  const handleEnterKeyDown = (event) => {
    if (event.key === 'Enter' && !!submitFilterRef?.current) {
      submitFilterRef.current.click()
    }
  }

  const columns = [
    {
      name: 'codeTypeName',
      key_name: 'codeTypeName',
      label: intl.formatMessage({ id: 'Fields.label.view.CodeType' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false
      }
    },
    {
      name: 'itemCode',
      key_name: 'ItemCode',
      isCustomTextFieldFilter: true,
      label: intl.formatMessage({ id: 'Fields.label.view.codeLookupValue' }),
      options: {
        filter: true,
        filterType: 'custom',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let itemCode = listingData[dataIndex]?.itemCode
          return !!itemCode &&
            <Button size={'large'} variant={'text'} color={'primary'} disableRipple
                    onClick={() => openDialog('show', listingData[dataIndex])}
                    className={classes.modifyLink}>{itemCode}</Button>
        },
        customFilterListOptions: {
          render: (v) => (
            <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.codeLookupValue'/>:
              <span>{v}</span>
            </div>
          )
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'Fields.label.view.codeLookupValue' })}
              id='ItemCode'
              name='ItemCode'
              value={filterList[index][0] || ''}
              onKeyDown={handleEnterKeyDown}
              onChange={(event) => {
                if (!!event.target.value) {
                  filterList[index][0] = event.target.value
                } else {
                  filterList[index] = []
                }
                onChange(filterList[index], index, column)
              }}
            />
          )
        }
      }
    },
    {
      name: `${locale === 'ar' ? 'codeNameSecondaryLang' || 'codeNamePrimaryLang' : 'codeNamePrimaryLang'}`,
      key_name: 'CodeName',
      isCustomTextFieldFilter: true,
      label: intl.formatMessage({ id: 'Fields.label.view.CodeNameCategory' }),
      options: {
        filter: true,
        filterType: 'custom',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let codeName = locale === 'ar' ? (listingData[dataIndex]?.codeNameSecondaryLang || listingData[dataIndex]?.codeNamePrimaryLang) : listingData[dataIndex]?.codeNamePrimaryLang
          let CategoryName = locale === 'ar' ? (listingData[dataIndex]?.parentCodeNameSecondaryLang || listingData[dataIndex]?.parentCodeNamePrimaryLang) : listingData[dataIndex]?.parentCodeNamePrimaryLang
          return !!codeName &&
            <div className={classes.cellDataWrapper}>
              <strong>{codeName}</strong>
              {!!CategoryName && <span>{CategoryName}</span>}
            </div>
        },
        customFilterListOptions: {
          render: (v) => (
            <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.CodeName'/>:
              <span>{v}</span>
            </div>
          )
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'Fields.label.view.CodeName' })}
              id='CodeName'
              name='CodeName'
              value={filterList[index][0] || ''}
              onKeyDown={handleEnterKeyDown}
              onChange={(event) => {
                if (!!event.target.value) {
                  filterList[index][0] = event.target.value
                } else {
                  filterList[index] = []
                }
                onChange(filterList[index], index, column)
              }}
            />
          )
        }
      }
    },
    {
      name: 'parentCodeNamePrimaryLang',
      key_name: 'ParentCodeName',
      isCustomTextFieldFilter: true,
      label: intl.formatMessage({ id: 'Fields.label.view.ParentCodeName' }),
      options: {
        filter: true,
        filterType: 'custom',
        display: 'false',
        viewColumns: false,
        print: false,
        sort: false,
        download: false,
        customFilterListOptions: {
          render: (v) => (
            <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.ParentCodeName'/>:
              <span>{v}</span>
            </div>
          )
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'Fields.label.view.ParentCodeName' })}
              id='ParentCodeName'
              name='ParentCodeName'
              value={filterList[index][0] || ''}
              onKeyDown={handleEnterKeyDown}
              onChange={(event) => {
                if (!!event.target.value) {
                  filterList[index][0] = event.target.value
                } else {
                  filterList[index] = []
                }
                onChange(filterList[index], index, column)
              }}
            />
          )
        }
      }
    },
    {
      name: `${locale === 'ar' ? 'descriptionSecondaryLang' || 'descriptionPrimaryLang' : 'descriptionPrimaryLang'}`,
      key_name: 'CodeDescription',
      isCustomTextFieldFilter: true,
      label: intl.formatMessage({ id: 'Fields.label.view.CodeDescription' }),
      options: {
        filter: true,
        filterType: 'custom',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let codeDescription = locale === 'ar' ? (listingData[dataIndex]?.descriptionSecondaryLang || listingData[dataIndex]?.descriptionPrimaryLang) : listingData[dataIndex]?.descriptionPrimaryLang
          return !!codeDescription &&
            <div className={classes.cellDataWrapper}>
              <span>{codeDescription}</span>
            </div>
        },
        customFilterListOptions: {
          render: (v) => (
            <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.CodeDescription'/>:
              <span>{v}</span>
            </div>
          )
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'Fields.label.view.CodeDescription' })}
              id='CodeDescription'
              name='CodeDescription'
              value={filterList[index][0] || ''}
              onKeyDown={handleEnterKeyDown}
              onChange={(event) => {
                if (!!event.target.value) {
                  filterList[index][0] = event.target.value
                } else {
                  filterList[index] = []
                }
                onChange(filterList[index], index, column)
              }}
            />
          )
        }
      }
    },
    {
      name: 'requesterTaxpayer',
      key_name: 'RequesterTaxpayerName',
      label: intl.formatMessage({ id: 'Fields.label.view.codeRequesterTaxpayer' }),
      options: {
        filter: false,
        // filterType: 'custom',
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let codeRequesterTaxpayerName = listingData[dataIndex]?.requesterTaxpayer[`${locale === 'ar' ? 'nameAr' || 'name' : 'name'}`]
          let codeRequesterTaxpayerTRN = listingData[dataIndex]?.requesterTaxpayer?.rin
          return !!codeRequesterTaxpayerName &&
            <div className={classes.cellDataWrapper}>
              <strong>{codeRequesterTaxpayerName}</strong>
              {!!codeRequesterTaxpayerTRN && <span>{codeRequesterTaxpayerTRN}</span>}
            </div>
        }
        // customFilterListOptions: {
        //   render: (v) => (
        //     <div className={classes.FilterChip}>
        //       <FormattedMessage id='Fields.label.view.CodeRequesterTaxpayerName'/>:
        //       <span>{v}</span>
        //     </div>
        //   )
        // },
        // filterOptions: {
        //   display: (filterList, onChange, index, column) => (
        //     <TextField
        //       fullWidth
        //       label={intl.formatMessage({ id: 'Fields.label.view.CodeRequesterTaxpayerName' })}
        //       id='RequesterTaxpayerName'
        //       name='RequesterTaxpayerName'
        //       value={filterList[index][0] || ''}
        //       onKeyDown={handleEnterKeyDown}
        //       onChange={(event) => {
        //         if (!!event.target.value) {
        //           filterList[index][0] = event.target.value
        //         } else {
        //           filterList[index] = []
        //         }
        //         onChange(filterList[index], index, column)
        //       }}
        //     />
        //   )
        // }
      }
    },
    {
      name: 'ownerTaxpayer',
      key_name: 'ownerTaxpayer',
      label: intl.formatMessage({ id: 'Fields.label.view.codeOwnerTaxpayer' }),
      options: {
        filter: false,
        // filterType: 'custom',
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let codeOwnerTaxpayerName = listingData[dataIndex]?.ownerTaxpayer[`${locale === 'ar' ? 'nameAr' || 'name' : 'name'}`]
          let codeOwnerTaxpayerTRN = listingData[dataIndex]?.ownerTaxpayer?.rin
          return !!codeOwnerTaxpayerName &&
            <div className={classes.cellDataWrapper}>
              <strong>{codeOwnerTaxpayerName}</strong>
              {!!codeOwnerTaxpayerTRN && <span>{codeOwnerTaxpayerTRN}</span>}
            </div>
        }
      }
    },
    {
      name: 'activeFrom',
      key_name: 'ActiveFrom',
      label: intl.formatMessage({ id: 'Fields.label.view.codeActiveFrom' }),
      options: {
        filter: true,
        filterType: 'custom',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let value = listingData[dataIndex]?.activeFrom
          return !!value && <div className={classes.cellDataWrapper}>
            <Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                    format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{value}</Moment>
          </div>
        },
        customFilterListOptions: {
          render: (v) => (
            <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.codeActiveFrom'/>:
              <span><Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                            format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{v?.toString()}</Moment></span>
            </div>
          )
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale === 'ar' ? arSA : enUS}>
              <DatePicker
                autoOk
                fullWidth
                showTodayButton
                disableFuture
                format="dd-MM-yyyy"
                margin="dense"
                id="ActiveFrom"
                name="ActiveFrom"
                clearable
                label={intl.formatMessage({ id: 'Fields.label.view.codeActiveFrom' })}
                placeholder={intl.formatMessage({ id: 'Fields.label.view.codeActiveFrom' })}
                InputProps={{
                  onKeyDown: handleEnterKeyDown,
                  endAdornment:
                    <InputAdornment position="end">
                      <EventIcon/>
                    </InputAdornment>
                }}
                value={filterList[index][0] || null}
                animateYearScrolling
                onChange={(date) => {
                  if (!!date) {
                    filterList[index][0] = date
                  } else {
                    filterList[index] = []
                  }
                  onChange(filterList[index], index, column)
                }}
              />
            </MuiPickersUtilsProvider>
          )
        }
      }
    },
    {
      name: 'activeTo',
      key_name: 'ActiveTo',
      label: intl.formatMessage({ id: 'Fields.label.view.codeActiveTo' }),
      options: {
        filter: true,
        filterType: 'custom',
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let value = listingData[dataIndex]?.activeTo
          return !!value && <div className={classes.cellDataWrapper}>
            <Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                    format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{value}</Moment>
          </div>
        },
        customFilterListOptions: {
          render: (v) => (
            <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.codeActiveTo'/>:
              <span><Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                            format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{v?.toString()}</Moment></span>
            </div>
          )
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale === 'ar' ? arSA : enUS}>
              <DatePicker
                autoOk
                fullWidth
                showTodayButton
                format="dd-MM-yyyy"
                margin="dense"
                id="ActiveTo"
                name="ActiveTo"
                clearable
                label={intl.formatMessage({ id: 'Fields.label.view.codeActiveTo' })}
                placeholder={intl.formatMessage({ id: 'Fields.label.view.codeActiveTo' })}
                InputProps={{
                  onKeyDown: handleEnterKeyDown,
                  endAdornment:
                    <InputAdornment position="end">
                      <EventIcon/>
                    </InputAdornment>
                }}
                value={filterList[index][0] || null}
                animateYearScrolling
                onChange={(date) => {
                  if (!!date) {
                    filterList[index][0] = date
                  } else {
                    filterList[index] = []
                  }
                  onChange(filterList[index], index, column)
                }}
              />
            </MuiPickersUtilsProvider>
          )
        }
      }
    },
    {
      name: 'active',
      key_name: 'Active',
      label: intl.formatMessage({ id: 'Fields.label.view.codeActive' }),
      options: {
        filter: true,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let value = listingData[dataIndex]?.active
          return <span
            className={classes.status + (!!value ? ' active' : ' not-active')}>{!!value ? intl.formatMessage({ id: 'Fields.label.view.codeActive.true' }) : intl.formatMessage({ id: 'Fields.label.view.codeActive.false' })}</span>
        },
        filterOptions: {
          names: [false, true],
          renderValue: v => !!v ? intl.formatMessage({ id: 'Fields.label.view.codeActive.true' }) : intl.formatMessage({ id: 'Fields.label.view.codeActive.false' })
        },
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Fields.label.view.codeActive'/>:
                <span>{!!v ? intl.formatMessage({ id: 'Fields.label.view.codeActive.true' }) : intl.formatMessage({ id: 'Fields.label.view.codeActive.false' })}</span>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'status',
      key_name: 'Status',
      isCustomTextFieldFilter: true,
      label: intl.formatMessage({ id: 'Fields.label.view.codeStatus' }),
      options: {
        filter: true,
        filterType: 'custom',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let status = listingData[dataIndex]?.status
          status = codeStatus?.filter(item => item.key === status)
          return (
            !!status && !!status.length &&
            <div
              className={classes.statusWrapper + ` ${status[0]['key']?.toString()?.toLowerCase()}`}>
              {getStatusIcon(status[0]['key'])}
              <strong>{status[0][`name_${locale}`]}</strong>
            </div>
          )
        },
        customFilterListOptions: {
          render: (v) => {
            let valueArr = v?.length && codeStatus?.length ? codeStatus.filter((item) => item.key.toString().toLowerCase() === v[0].toString().toLowerCase()) : []
            return !!valueArr && !!valueArr.length && <div className={classes.FilterChip}>
              <FormattedMessage id='Fields.label.view.codeStatus'/>:
              <div className={classes.statusWrapper + ` ${valueArr[0]['key']?.toString().toLowerCase()}`}>
                {getStatusIcon(valueArr[0]['key'])}
                <span>{valueArr[0][`name_${locale}`]}</span>
              </div>
            </div>
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <>
              <InputLabel id="Status-label" className={'MuiInputLabel-shrink'}><FormattedMessage
                id='Fields.label.view.codeStatus'/></InputLabel>
              <Select
                fullWidth
                displayEmpty
                labelId="Status-label"
                id="Status"
                name="Status"
                MenuProps={{ disableScrollLock: true }}
                value={filterList[index][0] || ''}
                label={intl.formatMessage({ id: 'Fields.label.view.codeStatus' })}
                onChange={(event) => {
                  if (!!event.target.value) {
                    filterList[index][0] = event.target.value
                  } else {
                    filterList[index] = []
                  }
                  onChange(filterList[index], index, column)
                }}
              >
                <MenuItem value={''}><FormattedMessage id='Table.List.Table.All'/></MenuItem>
                {codeStatus?.length && codeStatus.map((item) => (
                  !!item.key &&
                  <MenuItem key={item.key} value={item.key}>
                    <div className={classes.statusWrapper + ` ${item['key']?.toString().toLowerCase()}`}>
                      {getStatusIcon(item['key'])}
                      <span>{item[`name_${locale}`]}</span>
                    </div>
                  </MenuItem>))}
              </Select>
            </>
          )
        }
      }
    },
    {
      name: '',
      options: {
        filter: false,
        print: false,
        sort: false,
        viewColumns: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return listingData[dataIndex]?.ownerTaxpayer?.rin?.toString() === CookiesStorageService.getTaxpayerTRN()?.toString() && ['Submitted', 'Approved'].includes(listingData[dataIndex]?.status?.toString()) && !!listingData[dataIndex].active &&
            <IconButton color="secondary" aria-label="Edit" onClick={(e) => {
              !!e?.preventDefault && e.preventDefault()
              !!e?.stopPropagation && e.stopPropagation()
              loadCodeData(listingData[dataIndex]?.codeTypeName, listingData[dataIndex]?.itemCode, listingData[dataIndex]?.requesterTaxpayer || {}, listingData[dataIndex]?.ownerTaxpayer || {}, listingData[dataIndex]?.status || '', null, null, listingData[dataIndex])
              openDialog('edit', listingData[dataIndex])
            }}>
              <EditIcon/>
            </IconButton>
        }
      }
    }
  ]


  return (
    <>
      <LanguageConsumer>
        {({ locale }) => (
          <ListingConsumer>
            {({
                listingCount, listingRowsPerPage, listingPage, handleChangeListingPage, handleChangeListingRowsPerPage,
                handleListingFilterSubmit, listingLoading
              }) => (
              <div className={'listing-wrapper'}>
                <GeneralTableWithFilter
                  columns={columns}
                  type={'Codes'}
                  submitFilterRef={submitFilterRef}
                  listingData={listingData}
                  listingCount={listingCount}
                  listingRowsPerPage={listingRowsPerPage}
                  listingPage={listingPage}
                  handleChangeListingPage={handleChangeListingPage}
                  handleChangeListingRowsPerPage={handleChangeListingRowsPerPage}
                  handleListingFilterSubmit={handleListingFilterSubmit}
                  locale={locale}
                  titleKey={'CodesRequestsApprovalContent.PageTitle'}
                />
                {!!listingLoading && <FormLoader loading={true}/>}
              </div>
            )}
          </ListingConsumer>
        )}
      </LanguageConsumer>
    </>
  )
}

export default CodesRequestsApprovalContent
